import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Card, CardHeader, CardBody, Button, FormGroup, Label, Input, Row, Col } from "reactstrap";
import axios from "../../config/axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const MetaConnectSelect = () => {
  const history = useHistory();
  const location = useLocation();
  const { token: authToken } = useSelector((state) => state.auth);
  const [token, setToken] = useState("");
  const [adAccounts, setAdAccounts] = useState([]);
  const [pages, setPages] = useState([]);
  const [existingAccountId, setExistingAccountId] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState("");

  useEffect(() => {
    if (!authToken) return;
    axios
      .get("/ad-accounts", { headers: { Authorization: `Bearer ${authToken}` } })
      .then((res) => {
        const first = (res.data?.data || [])[0];
        const acct = first?.accountId || "";
        setExistingAccountId(acct);
      })
      .catch(() => setExistingAccountId(""));
  }, [authToken]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const t = params.get("token") || "";
    setToken(t);
    if (!t) {
      setLoading(false);
      return;
    }
    if (!authToken) {
      setLoading(false);
      return;
    }
    axios
      .get(`/auth/meta-ads/pending-accounts?token=${encodeURIComponent(t)}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        const list = res.data.adAccounts || [];
        setAdAccounts(list);
        const p = res.data.pages || [];
        setPages(p);

        // Auto-select account (prefer already-connected ad account id, otherwise first available).
        const normalizedExisting = String(existingAccountId || "").trim();
        const match =
          normalizedExisting && list.find((a) => String(a.id) === normalizedExisting);
        if (match) setSelectedAccountId(match.id);
        else if (list.length > 0) setSelectedAccountId(list[0].id);
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Could not load Meta accounts");
        setAdAccounts([]);
        setPages([]);
      })
      .finally(() => setLoading(false));
  }, [location.search, authToken, existingAccountId]);

  const handleConnect = () => {
    if (!token || !selectedAccountId) {
      toast.error("Could not determine an ad account to connect. Please connect again.");
      return;
    }
    setSubmitting(true);
    axios
      .post(
        "/auth/meta-ads/confirm-account",
        { token, accountId: selectedAccountId },
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      .then(() => {
        toast.success("Meta account connected");
        history.push("/seller/products?meta_connected=1");
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Connect failed");
        setSubmitting(false);
      });
  };

  if (!authToken) {
    return (
      <div className="animated fadeIn p-4">
        <Card>
          <CardBody>
            <p>Please log in to connect your Meta account.</p>
            <Button color="primary" onClick={() => history.push("/auth/login")}>
              Log in
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  const startConnect = () => {
    setConnectLoading(true);
    axios
      .get("/auth/meta-ads/connect", { headers: { Authorization: `Bearer ${authToken}` } })
      .then((res) => {
        if (res.data && res.data.url) {
          window.location.href = res.data.url;
          return;
        }
        setConnectLoading(false);
        toast.error(res.data?.message || "Could not start Meta connect");
      })
      .catch((err) => {
        setConnectLoading(false);
        toast.error(err.response?.data?.message || "Connect failed");
      });
  };

  if (!token) {
    return (
      <div className="animated fadeIn p-4">
        <ToastContainer />
        <Card>
          <CardHeader><b>Connect Meta account</b></CardHeader>
          <CardBody>
            <p>Connect your Meta (Facebook) account to advertise. You will log in with Facebook and then choose which ad account to use.</p>
            <Button color="primary" onClick={startConnect} disabled={connectLoading}>
              {connectLoading ? "Redirecting..." : "Connect Meta account"}
            </Button>
            <Button color="secondary" className="ml-2" onClick={() => history.push("/seller/products")}>
              Back to Products
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="animated fadeIn p-4 text-center">
        <i className="fas fa-spinner fa-pulse fa-2x" style={{ color: "var(--primary)" }} aria-hidden />
        <p className="mt-2">Loading your Meta accounts...</p>
      </div>
    );
  }

  return (
    <div className="animated fadeIn">
      <ToastContainer />
      <Row>
        <Col>
          <Card>
            <CardHeader>
              <b>Confirm Meta connection</b>
            </CardHeader>
            <CardBody>
              {adAccounts.length === 0 ? (
                <>
                  <p className="text-muted">No ad accounts found, or this link expired. Create an ad account in Meta Business Manager, then connect again.</p>
                  <Button color="primary" onClick={startConnect} disabled={connectLoading}>
                    {connectLoading ? "Redirecting..." : "Connect Meta account"}
                  </Button>
                  <Button color="secondary" className="ml-2" onClick={() => history.push("/seller/products")}>
                    Back to Products
                  </Button>
                </>
              ) : (
                <>
                  <p>We found your connected Facebook Page(s). Click Continue to finish connecting Meta.</p>

                  <FormGroup>
                    <Label>Facebook Page</Label>
                    {pages.length === 0 ? (
                      <div className="text-muted">
                        No Pages were returned by Meta. You can still continue, but campaign creation will require adding a Page manually.
                      </div>
                    ) : (
                      <div>
                        {pages.map((p) => (
                          <div key={p.pageId}>
                            <b>{p.pageName || "Page"}</b> <span className="text-muted">({p.pageId})</span>
                            {p.isPrimary ? <span className="text-muted"> — primary</span> : null}
                          </div>
                        ))}
                      </div>
                    )}
                  </FormGroup>

                  <Button color="primary" onClick={handleConnect} disabled={submitting}>
                    {submitting ? "Continuing..." : "Continue"}
                  </Button>
                  <Button color="secondary" className="ml-2" onClick={() => history.push("/seller/products")}>
                    Cancel
                  </Button>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default MetaConnectSelect;
